import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  InputAdornment,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Container,
} from "@mui/material";
import {
  Add,
  Search,
  MoreVert,
  Visibility,
  Edit,
  Business,
  LocationOn,
  Phone,
  Email,
  Person,
} from "@mui/icons-material";
import { departmentService } from "../../services/departmentService";
import { useSnackbar } from "notistack";

export const DepartmentList = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    pages: 0,
  });
  
  // Filter state
  const [filters, setFilters] = useState({
    isActive: '',
    departmentType: ''
  });

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDepartments();
    }, 500);

    return () => clearTimeout(timer);
  }, [pagination.page, pagination.limit, searchTerm, filters.isActive, filters.departmentType]);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.isActive !== '' && { isActive: filters.isActive }),
        ...(filters.departmentType && { departmentType: filters.departmentType })
      };

      const response = await departmentService.getDepartments(params);
      const { departments: departmentsData, pagination: paginationData } = response.data.data;
      
      setDepartments(departmentsData);
      setPagination(prev => ({
        ...prev,
        total: paginationData?.total,
        pages: paginationData?.pages
      }));
    } catch (error) {
      enqueueSnackbar("Error fetching departments", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleMenuOpen = (event, department) => {
    setAnchorEl(event.currentTarget);
    setSelectedDepartment(department);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDepartment(null);
  };

  const handleView = () => {
    if (selectedDepartment) {
      navigate(`/hospital/hospital-inventory/departments/${selectedDepartment._id}`);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedDepartment) {
      navigate(`/hospital/hospital-inventory/departments/${selectedDepartment._id}/edit`);
    }
    handleMenuClose();
  };

  const getInitials = (departmentName) => {
    const words = departmentName?.split(' ') || [];
    if (words.length >= 2) {
      return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
    }
    return departmentName?.substring(0, 2).toUpperCase() || "";
  };

  const getDepartmentTypeColor = (type) => {
    const colors = {
      production: 'primary',
      warehouse: 'secondary',
      maintenance: 'warning',
      administration: 'info',
      research: 'success',
      other: 'default'
    };
    return colors[type] || 'default';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 6,
        }}
      >
        <Box>
          <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
            Departments
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mt: 1 }}>
            Manage your hospital departments and their information
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<Add />}
          onClick={() => navigate("/hospital/hospital-inventory/departments/new")}
          sx={{ 
            fontSize: '1.1rem',
            py: 1.5,
            px: 3,
            borderRadius: 3
          }}
        >
          Add Department
        </Button>
      </Box>

      {/* Search and Filters Card */}
      <Card 
        sx={{ 
          mb: 4, 
          borderRadius: 3,
          boxShadow: 3
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search departments by name, code, or contact..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: '1.5rem' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '1.1rem' }}>Department Type</InputLabel>
                <Select
                  value={filters.departmentType}
                  label="Department Type"
                  onChange={(e) => handleFilterChange('departmentType', e.target.value)}
                  sx={{
                    fontSize: '1.1rem',
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '1.1rem' }}>All Types</MenuItem>
                  <MenuItem value="production" sx={{ fontSize: '1.1rem' }}>Production</MenuItem>
                  <MenuItem value="warehouse" sx={{ fontSize: '1.1rem' }}>Warehouse</MenuItem>
                  <MenuItem value="maintenance" sx={{ fontSize: '1.1rem' }}>Maintenance</MenuItem>
                  <MenuItem value="administration" sx={{ fontSize: '1.1rem' }}>Administration</MenuItem>
                  <MenuItem value="research" sx={{ fontSize: '1.1rem' }}>Research</MenuItem>
                  <MenuItem value="other" sx={{ fontSize: '1.1rem' }}>Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '1.1rem' }}>Status</InputLabel>
                <Select
                  value={filters.isActive}
                  label="Status"
                  onChange={(e) => handleFilterChange('isActive', e.target.value)}
                  sx={{
                    fontSize: '1.1rem',
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '1.1rem' }}>All</MenuItem>
                  <MenuItem value="true" sx={{ fontSize: '1.1rem' }}>Active</MenuItem>
                  <MenuItem value="false" sx={{ fontSize: '1.1rem' }}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography 
                variant="h6" 
                color="primary" 
                align="center"
                sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}
              >
                Total: {pagination.total} departments
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Departments Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {departments.map((department) => (
              <Grid item xs={12} sm={6} lg={4} key={department._id}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "0.3s",
                    borderRadius: 3,
                    boxShadow: 2,
                    "&:hover": { 
                      transform: "translateY(-8px)", 
                      boxShadow: 8 
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Header with Avatar and Menu */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 3,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                          sx={{ 
                            bgcolor: "primary.main", 
                            width: 64, 
                            height: 64,
                            fontSize: '1.5rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {getInitials(department.departmentName)}
                        </Avatar>
                        <Box>
                          <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.4rem' }}>
                            {department.departmentName}
                          </Typography>
                          <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                            {department.departmentCode}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        size="large"
                        onClick={(e) => handleMenuOpen(e, department)}
                        sx={{ fontSize: '1.5rem' }}
                      >
                        <MoreVert fontSize="inherit" />
                      </IconButton>
                    </Box>

                    {/* Department Details */}
                    <Box sx={{ mb: 3 }}>
                      <Chip
                        label={department.departmentType}
                        size="medium"
                        color={getDepartmentTypeColor(department.departmentType)}
                        sx={{ 
                          mb: 2,
                          fontSize: '1rem',
                          height: '32px'
                        }}
                      />
                      
                      {/* Location */}
                      {department.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <LocationOn sx={{ fontSize: '1.3rem', color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                            {department.location.building}
                            {department.location.floor && `, Floor ${department.location.floor}`}
                            {department.location.room && `, Room ${department.location.room}`}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Phone */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Phone sx={{ fontSize: '1.3rem', color: 'text.secondary', mr: 1 }} />
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                          {department.contactPhone}
                        </Typography>
                      </Box>
                      
                      {/* Email */}
                      {department.contactEmail && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Email sx={{ fontSize: '1.3rem', color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                            {department.contactEmail}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Contact Person */}
                      {department.contactPerson && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Person sx={{ fontSize: '1.3rem', color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                            {department.contactPerson}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Manager */}
                    {department.manager && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                          <strong>Manager:</strong> {department.manager.name || 'N/A'}
                        </Typography>
                      </Box>
                    )}

                    {/* Footer */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 2,
                      }}
                    >
                      <Chip
                        label={department.isActive ? "Active" : "Inactive"}
                        size="medium"
                        color={department.isActive ? "success" : "default"}
                        sx={{ fontSize: '1rem', height: '32px' }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                        ID: {department.departmentId}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* No Results */}
          {departments.length === 0 && !loading && (
            <Card sx={{ 
              textAlign: "center", 
              py: 8, 
              borderRadius: 3,
              boxShadow: 3
            }}>
              <CardContent>
                <Business sx={{ fontSize: 80, color: "text.secondary", mb: 3 }} />
                <Typography variant="h4" color="text.secondary" gutterBottom sx={{ fontSize: '1.8rem' }}>
                  No departments found
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mb: 4 }}>
                  {searchTerm || filters.isActive !== '' || filters.departmentType
                    ? "Try adjusting your search or filter terms"
                    : "Get started by adding your first department"}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Add />}
                  onClick={() => navigate("/hospital/hospital-inventory/departments/new")}
                  sx={{ 
                    fontSize: '1.1rem',
                    py: 1.5,
                    px: 4,
                    borderRadius: 3
                  }}
                >
                  Add First Department
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '1.1rem',
                  }
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3,
          }
        }}
      >
        <MenuItem onClick={handleView} sx={{ fontSize: '1.1rem', py: 1.5 }}>
          <Visibility sx={{ mr: 2, fontSize: '1.3rem' }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEdit} sx={{ fontSize: '1.1rem', py: 1.5 }}>
          <Edit sx={{ mr: 2, fontSize: '1.3rem' }} />
          Edit Department
        </MenuItem>
      </Menu>
    </Container>
  );
};